import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Activity, CalendarDays, CheckSquare, ChevronRight, Clock, FileText, TrendingUp, WrenchIcon } from 'lucide-react';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Customer } from '../components/CustomerCard';
import { Task } from '../components/TaskCard';
import { Invoice } from '../components/InvoiceCard';
import { Earning } from '../components/EarningCard';
import { Payout } from '../components/PayoutCard';
import { RichTextEditor } from '../components/RichTextEditor';
interface DashboardProps {
  customers: Customer[];
  tasks: Task[];
  invoices: Invoice[];
  earnings: Earning[];
  payouts: Payout[];
  onAddTask: (task: Omit<Task, 'id' | 'customerName'>) => void;
  onEditTask: (task: Task) => void;
  onViewCustomer: (customerId: string) => void;
}
type Category = 'Samlet' | 'Ladeboks' | 'Andet';
const STATUS_STYLE: Record<string, {
  label: string;
  chip: string;
  dot: string;
}> = {
  planned: {
    label: 'Planlagt',
    chip: 'bg-blue-50 text-blue-700',
    dot: 'bg-blue-500'
  },
  pending: {
    label: 'Afventende',
    chip: 'bg-purple-50 text-purple-700',
    dot: 'bg-purple-500'
  },
  'in-progress': {
    label: 'I gang',
    chip: 'bg-amber-50 text-amber-700',
    dot: 'bg-amber-500'
  },
  done: {
    label: 'Afsluttet',
    chip: 'bg-emerald-50 text-emerald-700',
    dot: 'bg-emerald-500'
  }
};

/** Lists start short so the phone view isn't one long scroll. */
const COLLAPSED_COUNT = 3;
export function Dashboard({
  customers,
  tasks,
  invoices,
  onEditTask
}: DashboardProps) {
  const [category, setCategory] = useState<Category>('Samlet');
  const [notes, setNotes] = useState('');
  const [showAllWeek, setShowAllWeek] = useState(false);
  const [showAllActive, setShowAllActive] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches);
  useEffect(() => {
    const query = window.matchMedia('(min-width: 1024px)');
    const onChange = (event: MediaQueryListEvent) => setIsDesktop(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  // Notes stay in sync across devices.
  useEffect(() => {
    const ref = doc(db, 'settings', 'dashboard-notes');
    return onSnapshot(ref, snapshot => {
      if (snapshot.exists()) setNotes(snapshot.data().content || '');
    }, error => console.error('Error loading notes:', error));
  }, []);
  useEffect(() => {
    const timeout = setTimeout(async () => {
      try {
        await setDoc(doc(db, 'settings', 'dashboard-notes'), {
          content: notes,
          updatedAt: new Date().toISOString()
        });
      } catch (error) {
        console.error('Error saving notes:', error);
      }
    }, 1000);
    return () => clearTimeout(timeout);
  }, [notes]);
  const statistics = useMemo(() => {
    const filtered = category === 'Samlet' ? tasks : tasks.filter(t => t.category === category);
    const revenue = filtered.reduce((sum, t) => sum + (Number(t.totalPrice) || 0), 0);
    const hours = filtered.reduce((sum, t) => sum + (Number(t.hours) || 0), 0);
    return {
      revenue,
      hours,
      averageHourlyRate: hours > 0 ? revenue / hours : 0,
      openTasks: filtered.filter(t => t.status !== 'done').length
    };
  }, [tasks, category]);
  const thisWeek = useMemo(() => {
    const now = new Date();
    const day = now.getDay();
    const monday = new Date(now);
    monday.setDate(now.getDate() + (day === 0 ? -6 : 1 - day));
    monday.setHours(0, 0, 0, 0);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    const appointments: {
      task: Task;
      date: Date;
      type: 'Projektering' | 'Installation';
    }[] = [];
    tasks.forEach(task => {
      const entries: [string | undefined, 'Projektering' | 'Installation'][] = [[task.agreedPlanning, 'Projektering'], [task.agreedInstallation, 'Installation']];
      entries.forEach(([value, type]) => {
        if (!value) return;
        const date = new Date(value);
        if (date >= monday && date <= sunday) {
          appointments.push({
            task,
            date,
            type
          });
        }
      });
    });
    return appointments.sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [tasks]);
  const recentActive = useMemo(() => [...tasks].filter(t => t.status !== 'done').sort((a, b) => {
    const aDate = a.agreedInstallation || a.agreedPlanning || '';
    const bDate = b.agreedInstallation || b.agreedPlanning || '';
    if (aDate && bDate) return new Date(bDate).getTime() - new Date(aDate).getTime();
    if (aDate) return -1;
    if (bDate) return 1;
    return parseInt(b.taskId?.substring(1) || '0') - parseInt(a.taskId?.substring(1) || '0');
  }).slice(0, 5), [tasks]);
  const statusDistribution = useMemo(() => {
    const keys = ['planned', 'pending', 'in-progress', 'done'] as const;
    const items = keys.map(key => ({
      key,
      ...STATUS_STYLE[key],
      count: tasks.filter(t => t.status === key).length
    }));
    return {
      total: tasks.length,
      items: items.map(item => ({
        ...item,
        percentage: tasks.length > 0 ? item.count / tasks.length * 100 : 0
      }))
    };
  }, [tasks]);
  const kr = (value: number) => `${value.toLocaleString('da-DK')} kr`;
  return <div className="space-y-6" data-id="element-1453" data-mp="10xgj17p8pabo">
      {/* Header */}
      <header className="flex flex-wrap items-end justify-between gap-4" data-id="element-1454" data-mp="15w0290cg880c">
        <div data-id="element-1455" data-mp="1at9xwxqflfwm">
          <h1 className="text-3xl font-bold tracking-tight text-stone-900" data-id="element-1456" data-mp="1auuueswb38x4">
            Overblik
          </h1>
          <p className="mt-1.5 text-stone-500" data-id="element-1457" data-mp="l6fm601qn79y">
            {customers.length} kunder · {statistics.openTasks} åbne opgaver ·{' '}
            {invoices.length} fakturaer
          </p>
        </div>

        <div role="group" aria-label="Filtrer nøgletal" className="flex rounded-lg border border-stone-200 bg-white p-1" data-id="element-1458" data-mp="uw8xczxyxkdo">
          {(['Samlet', 'Ladeboks', 'Andet'] as Category[]).map(option => <button key={option} onClick={() => setCategory(option)} aria-pressed={category === option} className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${category === option ? 'bg-stone-900 text-white' : 'text-stone-500 hover:text-stone-900'}`} data-id="element-1459" data-mp="yx2oe26pjfas">
              {option === 'Andet' ? 'Alm. Installation' : option}
            </button>)}
        </div>
      </header>

      {/* Key figures — revenue leads, the rest support it */}
      <section aria-label="Nøgletal" className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-stone-200 bg-stone-200 lg:grid-cols-4" data-id="element-1460" data-mp="1q6x74geoxqbc">
        <div className="bg-white p-5 lg:p-6" data-id="element-1461" data-mp="1gehpjk7b33qz">
          <p className="text-xs font-semibold uppercase tracking-wider text-stone-400" data-id="element-1462" data-mp="1ldaz51jh2alv">
            Omsætning
          </p>
          <p className="mt-2 text-3xl font-bold tracking-tight text-stone-900 lg:text-4xl" data-id="element-1463" data-mp="17da0lsqojf9k">
            {kr(statistics.revenue)}
          </p>
        </div>
        {[{
        label: 'Timer',
        value: `${statistics.hours.toLocaleString('da-DK')} t`,
        icon: Clock
      }, {
        label: 'Gns. timepris',
        value: `${statistics.averageHourlyRate.toLocaleString('da-DK', {
          maximumFractionDigits: 0
        })} kr/t`,
        icon: TrendingUp
      }, {
        label: 'Åbne opgaver',
        value: statistics.openTasks,
        icon: CheckSquare
      }].map(item => {
        const Icon = item.icon;
        return <div key={item.label} className="bg-white p-5 lg:p-6" data-id="element-1464" data-mp="1atl1yka3zr6m">
              <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-stone-400" data-id="element-1465" data-mp="1as04alshzs4j">
                <Icon className="h-3.5 w-3.5" data-id="element-1466" data-mp="15sx9du68f2aa" />
                {item.label}
              </p>
              <p className="mt-2 text-2xl font-semibold tracking-tight text-stone-900" data-id="element-1467" data-mp="10taoqzo2qjrg">
                {item.value}
              </p>
            </div>;
      })}
      </section>

      {/* Primary work region */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3" data-id="element-1468" data-mp="mw5lf7fqvyzw">
        {/* This week — the thing you act on */}
        <section className="overflow-hidden rounded-2xl border border-stone-200 bg-white lg:col-span-2" data-id="element-1469" data-mp="2m99pbcr9ys7y">
          <header className="flex items-center gap-2 border-b border-stone-100 px-5 py-4" data-id="element-1470" data-mp="2h9nch4fczdzo">
            <CalendarDays className="h-4 w-4 text-stone-400" data-id="element-1471" data-mp="2ou7p8wdeeifb" />
            <h2 className="flex-1 text-base font-semibold text-stone-900" data-id="element-1472" data-mp="2orlgwuionyw1">
              Denne uge
            </h2>
            {thisWeek.length > 0 && <span className="rounded-full bg-stone-100 px-2 py-0.5 text-xs font-medium text-stone-600" data-id="element-1473" data-mp="2ykk6ofpx1vh8">
                {thisWeek.length} aftaler
              </span>}
          </header>

          {thisWeek.length === 0 ? <div className="px-5 py-14 text-center" data-id="element-1474" data-mp="25wwpzbrrnozv">
              <CalendarDays className="mx-auto h-7 w-7 text-stone-200" data-id="element-1475" data-mp="2aupu7z9ie8xi" />
              <p className="mt-3 text-sm font-medium text-stone-900" data-id="element-1476" data-mp="2fs87g1uk7abb">
                Ingen aftaler i denne uge
              </p>
              <p className="mt-1 text-sm text-stone-500" data-id="element-1477" data-mp="2jv4jf9plg86a">
                Projekteringer og installationer vises her.
              </p>
            </div> : <ul className="divide-y divide-stone-100" data-id="element-1478" data-mp="3v8yv7ehdotg7">
              {(showAllWeek ? thisWeek : thisWeek.slice(0, COLLAPSED_COUNT)).map((appointment, index) => <li key={`${appointment.task.id}-${appointment.type}-${index}`} data-id="element-1479" data-mp="1q34cnwjbckyb" data-mpi={index}>
                  <button onClick={() => onEditTask(appointment.task)} className="group flex w-full items-center gap-4 px-5 py-3.5 text-left transition-colors hover:bg-stone-50" data-id="element-1480" data-mp="1uuw3m86dssoz">
                    <span className="flex w-11 flex-shrink-0 flex-col items-center rounded-lg border border-stone-200 bg-stone-50 py-1.5" data-id="element-1481" data-mp="1278nb5c58o85">
                      <span className="text-[10px] font-semibold uppercase text-stone-500" data-id="element-1482" data-mp="176v0xfvvw6hh">
                        {appointment.date.toLocaleDateString('da-DK', {
                    weekday: 'short'
                  })}
                      </span>
                      <span className="text-base font-bold leading-none text-stone-800" data-id="element-1483" data-mp="2nirzjao4mgkp">
                        {appointment.date.getDate()}
                      </span>
                    </span>

                    <span className="min-w-0 flex-1" data-id="element-1484" data-mp="guhyo3hzhyyv">
                      <span className="block truncate text-sm font-medium text-stone-900" data-id="element-1485" data-mp="bveqia6pjcmo">
                        {appointment.task.title}
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-stone-500" data-id="element-1486" data-mp="72vdbebxmjie">
                        {appointment.date.toLocaleTimeString('da-DK', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                        {' · '}
                        {appointment.task.customerName || 'Ingen kunde'}
                      </span>
                    </span>

                    <span className={`hidden flex-shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold sm:block ${appointment.type === 'Installation' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'}`} data-id="element-1487" data-mp="3fd6ms0rvmiqo">
                      {appointment.type}
                    </span>
                    <ChevronRight className="h-4 w-4 flex-shrink-0 text-stone-300 transition-colors group-hover:text-brand-600" data-id="element-1488" data-mp="3cts6mhqmblpu" />
                  </button>
                </li>)}

              {thisWeek.length > COLLAPSED_COUNT && <li data-id="element-1489" data-mp="3qs9q239hqc1y">
                  <button onClick={() => setShowAllWeek(!showAllWeek)} className="w-full px-5 py-3 text-sm font-medium text-stone-500 transition-colors hover:bg-stone-50 hover:text-brand-700" data-id="element-1490" data-mp="3qrqjtjt7dc3r">
                    {showAllWeek ? 'Vis mindre' : `Vis alle ${thisWeek.length} aftaler`}
                  </button>
                </li>}
            </ul>}
        </section>

        {/* Status distribution — compact supporting panel */}
        <section className="rounded-2xl border border-stone-200 bg-white p-5" data-id="element-1491" data-mp="2abduxbq6mbty">
          <h2 className="text-base font-semibold text-stone-900" data-id="element-1492" data-mp="seahui5r026h">
            Status-fordeling
          </h2>
          <p className="mt-0.5 text-sm text-stone-500" data-id="element-1493" data-mp="nj4v5h4iujhc">
            {statusDistribution.total} opgaver i alt
          </p>

          {statusDistribution.total === 0 ? <p className="mt-6 text-sm text-stone-500" data-id="element-1494" data-mp="xgjbzhucxaa6">
              Opret opgaver for at se fordelingen.
            </p> : <>
              <div className="mt-5 flex h-2 overflow-hidden rounded-full bg-stone-100" data-id="element-1495" data-mp="4qsy99wn0ael">
                {statusDistribution.items.filter(item => item.count > 0).map((item, index) => <motion.span key={item.key} initial={{
              width: 0
            }} animate={{
              width: `${item.percentage}%`
            }} transition={{
              duration: 0.5,
              delay: index * 0.04,
              ease: [0.23, 1, 0.32, 1]
            }} className={`${item.dot} border-r border-white last:border-r-0`} data-id="element-1496" data-mp="dq66jz90ysxq" data-mpi={index} />)}
              </div>

              <ul className="mt-5 space-y-2.5" data-id="element-1497" data-mp="8lvpadwy5eru">
                {statusDistribution.items.map(item => <li key={item.key} className="flex items-center gap-2.5" data-id="element-1498" data-mp="dfhqn9xntw8c">
                    <span className={`h-2 w-2 flex-shrink-0 rounded-full ${item.dot}`} data-id="element-1499" data-mp="ichxvo68x511" />
                    <span className="flex-1 text-sm text-stone-700" data-id="element-1500" data-mp="mgy0ngkkbwc1">
                      {item.label}
                    </span>
                    <span className="text-sm font-semibold text-stone-900" data-id="element-1501" data-mp="wcsrgyyxwxpw">
                      {item.count}
                    </span>
                  </li>)}
              </ul>
            </>}
        </section>
      </div>

      {/* Secondary region */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3" data-id="element-1502" data-mp="2qkwhxgdjogq1">
        <section className="overflow-hidden rounded-2xl border border-stone-200 bg-white lg:col-span-2" data-id="element-1503" data-mp="2qmfxf4rdmnqr">
          <header className="flex items-center gap-2 border-b border-stone-100 px-5 py-4" data-id="element-1504" data-mp="2lsv9k5jhza5a">
            <Activity className="h-4 w-4 text-stone-400" data-id="element-1505" data-mp="2gvlady6b3k49" />
            <h2 className="text-base font-semibold text-stone-900" data-id="element-1506" data-mp="2bwig98rv6yb2">
              Senest aktive opgaver
            </h2>
          </header>

          {recentActive.length === 0 ? <div className="px-5 py-14 text-center" data-id="element-1507" data-mp="34ko07tb3gj9u">
              <CheckSquare className="mx-auto h-7 w-7 text-stone-200" data-id="element-1508" data-mp="34l7b4q2bu7ed" />
              <p className="mt-3 text-sm font-medium text-stone-900" data-id="element-1509" data-mp="2zkrxryi1ozwe">
                Ingen aktive opgaver
              </p>
              <p className="mt-1 text-sm text-stone-500" data-id="element-1510" data-mp="2ulyiq1n410u4">
                Opgaver du arbejder på vises her.
              </p>
            </div> : <ul className="divide-y divide-stone-100" data-id="element-1511" data-mp="1j6tg0xmzb2ai">
              {(showAllActive ? recentActive : recentActive.slice(0, COLLAPSED_COUNT)).map(task => {
            const status = STATUS_STYLE[task.status] ?? {
              label: 'Todo',
              chip: 'bg-stone-100 text-stone-700',
              dot: 'bg-stone-400'
            };
            return <li key={task.id} data-id="element-1512" data-mp="678252qrfthu">
                    <button onClick={() => onEditTask(task)} className="group flex w-full items-center gap-3 px-5 py-3.5 text-left transition-colors hover:bg-stone-50" data-id="element-1513" data-mp="3npat67gd0yca">
                      <WrenchIcon className="h-4 w-4 flex-shrink-0 text-stone-300" data-id="element-1514" data-mp="3epnoncsc3cf0" />
                      <span className="min-w-0 flex-1" data-id="element-1515" data-mp="3jn613d5k89rr">
                        <span className="block truncate text-sm font-medium text-stone-900" data-id="element-1516" data-mp="3jll6xon5qsuf">
                          {task.title}
                        </span>
                        <span className="mt-0.5 block truncate text-xs text-stone-500" data-id="element-1517" data-mp="39ogjllhksrc1">
                          {task.customerName || 'Ingen kunde'}
                          {task.taskId && ` · ${task.taskId}`}
                        </span>
                      </span>
                      <span className={`flex-shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ${status.chip}`} data-id="element-1518" data-mp="302f8ryo2yiwp">
                        {status.label}
                      </span>
                      <ChevronRight className="h-4 w-4 flex-shrink-0 text-stone-300 transition-colors group-hover:text-brand-600" data-id="element-1519" data-mp="3910yqevvt07i" />
                    </button>
                  </li>;
          })}

              {recentActive.length > COLLAPSED_COUNT && <li data-id="element-1520" data-mp="3dt0tlsx3h2x8">
                  <button onClick={() => setShowAllActive(!showAllActive)} className="w-full px-5 py-3 text-sm font-medium text-stone-500 transition-colors hover:bg-stone-50 hover:text-brand-700" data-id="element-1521" data-mp="3dtu3v3v9iak4">
                    {showAllActive ? 'Vis mindre' : `Vis alle ${recentActive.length} opgaver`}
                  </button>
                </li>}
            </ul>}
        </section>

        {/* Notes stay out of the way on the phone, open by default on desktop */}
        <details open={isDesktop} className="group rounded-2xl border border-stone-200 bg-white [&_summary::-webkit-details-marker]:hidden" data-id="element-1522" data-mp="2nbdf5fo4yat3">
          <summary className="flex cursor-pointer list-none items-center gap-2 p-5 lg:cursor-default" data-id="element-1523" data-mp="1unhha7mtycuq">
            <FileText className="h-4 w-4 text-stone-400" data-id="element-1524" data-mp="1zmao5h7m35lx" />
            <h2 className="flex-1 text-base font-semibold text-stone-900" data-id="element-1525" data-mp="1zkr5jjyhgigz">
              Noter
            </h2>
            <ChevronRight className="h-4 w-4 text-stone-400 transition-transform group-open:rotate-90 lg:hidden" data-id="element-1526" data-mp="24fdlm0fiez7e" />
          </summary>
          <div className="px-5 pb-5" data-id="element-1527" data-mp="1bwgbp0r7fj27">
            <RichTextEditor label="" value={notes} onChange={setNotes} placeholder="Skriv dine noter her…" rows={6} data-id="element-1528" data-mp="1bvezuu2d6sjf" />
          </div>
        </details>
      </div>
    </div>;
}